import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, HardHat, Wrench, AlertCircle, TrendingUp, 
  Clock, ArrowRight, RefreshCw, Activity, Zap, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';

const BRONZE = '#C9A961';

const CommandCenterHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ government: null, gc: null, fm: null });
  const [waitingOnMe, setWaitingOnMe] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [govStats, gcStats, fmStats, womRes, activityRes] = await Promise.all([
        api.get('/command-center/stats/government').catch(() => ({ data: {} })),
        api.get('/command-center/stats/gc').catch(() => ({ data: {} })),
        api.get('/command-center/stats/fm').catch(() => ({ data: {} })),
        api.get('/command-center/waiting-on-me?limit=10').catch(() => ({ data: [] })),
        api.get('/command-center/activity-log?limit=15').catch(() => ({ data: [] }))
      ]);
      
      setStats({
        government: govStats.data,
        gc: gcStats.data,
        fm: fmStats.data
      });
      setWaitingOnMe(womRes.data || []);
      setRecentActivity(activityRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalBids = (stats.government?.total_opportunities || 0) + 
                    (stats.gc?.total_opportunities || 0) + 
                    (stats.fm?.total_opportunities || 0);
  
  const totalWaiting = (stats.government?.pending_waiting_on_me || 0) + 
                       (stats.gc?.pending_waiting_on_me || 0) + 
                       (stats.fm?.pending_waiting_on_me || 0);

  const totalPortals = (stats.government?.portals_active || 0) + 
                       (stats.gc?.portals_active || 0) + 
                       (stats.fm?.portals_active || 0);

  const divisions = [
    { id: 'government', name: 'GOVERNMENT', fullName: 'Government Contracting', icon: Building2, stats: stats.government },
    { id: 'gc', name: 'GC', fullName: 'General Contracting', icon: HardHat, stats: stats.gc },
    { id: 'fm', name: 'FM', fullName: 'Facility Maintenance', icon: Wrench, stats: stats.fm }
  ];

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getDivisionLabel = (divId) => {
    const labels = { government: 'GOV', gc: 'GC', fm: 'FM' };
    return labels[divId] || divId;
  };

  return (
    <div className="min-h-screen bg-black p-6">
      {/* Header with Logo */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img 
            src="/williams-logo-transparent.png" 
            alt="Williams Diversified" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="text-2xl font-bold text-white">OPERATIONS HUB</h1>
            <p className="text-neutral-500 text-sm">Williams Diversified Command Center</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={fetchAllData}
          className="border-neutral-700 text-neutral-400 hover:text-white hover:border-[#C9A961]"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Three Division Columns */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {divisions.map((div) => {
          const Icon = div.icon;
          const divStats = div.stats || {};
          return (
            <Card 
              key={div.id}
              className="bg-neutral-900/50 border-neutral-800 hover:border-[#C9A961]/50 cursor-pointer transition-all group"
              onClick={() => navigate(`/command-center/${div.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#C9A961]/10 border border-[#C9A961]/20">
                      <Icon className="h-5 w-5" style={{ color: BRONZE }} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">{div.name}</h2>
                      <p className="text-xs text-neutral-500">{div.fullName}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-neutral-600 group-hover:text-[#C9A961] transition-colors" />
                </div>
                
                <div className="h-px bg-neutral-800 mb-4" />
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-2xl font-bold text-white">{divStats.total_opportunities || 0}</p>
                    <p className="text-xs text-neutral-500">Active Bids</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">{divStats.pending_waiting_on_me || 0}</p>
                    <p className="text-xs text-neutral-500">Waiting</p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-neutral-500">{divStats.portals_active || 0} Portals</span>
                  <span style={{ color: BRONZE }}>{divStats.win_rate || 0}% Win Rate</span>
                </div>

                <Button 
                  className="w-full mt-4 bg-[#C9A961]/10 border border-[#C9A961]/30 text-[#C9A961] hover:bg-[#C9A961] hover:text-black transition-all"
                  size="sm"
                >
                  Enter {div.name}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Waiting On Me Section */}
      <Card className="bg-neutral-900/50 border-neutral-800 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <Zap className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">WAITING ON ME</h2>
                <p className="text-xs text-neutral-500">Items requiring your decision or action</p>
              </div>
            </div>
            <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
              {totalWaiting} Items
            </Badge>
          </div>

          {waitingOnMe.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No items waiting for your action</p>
            </div>
          ) : (
            <div className="space-y-2">
              {waitingOnMe.slice(0, 5).map((item) => (
                <div 
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-neutral-800 hover:border-[#C9A961]/30 cursor-pointer transition-colors"
                  onClick={() => navigate(`/command-center/${item.division_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <Zap className={`h-4 w-4 ${item.priority === 'high' || item.priority === 'urgent' ? 'text-red-400' : 'text-yellow-400'}`} />
                    <div>
                      <p className="text-white text-sm">{item.summary}</p>
                      <p className="text-xs text-neutral-500">{item.request_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-neutral-800 text-neutral-400 text-xs">
                      {getDivisionLabel(item.division_id)}
                    </Badge>
                    <span className="text-xs text-neutral-500">{getTimeAgo(item.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Row: Quick Stats + Recent Activity */}
      <div className="grid grid-cols-3 gap-4">
        {/* Quick Stats */}
        <Card className="bg-neutral-900/50 border-neutral-800">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-neutral-400 mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" style={{ color: BRONZE }} />
              SYSTEM OVERVIEW
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Total Bids</span>
                <span className="text-xl font-bold text-white">{totalBids}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Waiting On Me</span>
                <span className="text-xl font-bold text-red-400">{totalWaiting}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Active Portals</span>
                <span className="text-xl font-bold" style={{ color: BRONZE }}>{totalPortals}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity - Spans 2 columns */}
        <Card className="bg-neutral-900/50 border-neutral-800 col-span-2">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-neutral-400 mb-4 flex items-center gap-2">
              <Activity className="h-4 w-4" style={{ color: BRONZE }} />
              RECENT ACTIVITY
            </h3>
            <ScrollArea className="h-[180px]">
              {recentActivity.length === 0 ? (
                <p className="text-neutral-500 text-sm">No recent activity</p>
              ) : (
                <div className="space-y-2">
                  {recentActivity.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-2 border-b border-neutral-800/50 last:border-0">
                      <div className={`w-2 h-2 rounded-full ${
                        item.actor_type === 'agent' ? 'bg-[#C9A961]' : 'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-neutral-300 truncate">{item.details}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className="bg-neutral-800 text-neutral-400 text-xs">
                          {getDivisionLabel(item.division_id)}
                        </Badge>
                        <span className="text-xs text-neutral-600">{getTimeAgo(item.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommandCenterHub;
